import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "defaultValue", "onChange", "readOnly", "value"];
import PropTypes from 'prop-types';
import React, { Children, forwardRef, Fragment, useCallback, useState } from 'react';
import { Input } from '../Input/Input';
import { StyledReadOnlySelect } from './ReadOnlySelect.styles';

/**
 * Use this instead of `Select` to display a read-only `select` element.
 *
 * The [`HTML spec`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly)
 * doesn't actually support read-only `select` elements so, when set to the read-only state,
 * the `select` is converted into a read-only text `input` and an accompanying hidden input.
 * The text input displays the label text while the hidden `input` holds `value`, `name`,
 * `id` and `ref`.
 *
 * @since 8.27.0
 */
export var ReadOnlySelect = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    readOnly = _ref.readOnly,
    controlledValue = _ref.value,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultValue || ''),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledValue = _useState2[0],
    setUncontrolledValue = _useState2[1];
  var value = typeof controlledValue !== 'undefined' ? controlledValue : uncontrolledValue;
  var onInputChange = useCallback(function (e) {
    var changed = e.target.value;
    setUncontrolledValue(changed);
    if (onChange) {
      onChange(changed);
    }
  }, [onChange]);
  if (readOnly) {
    // Create flat array of <option> elements so they're easier to loop through
    var options = Children.map(children, function (child) {
      if (child.type === 'optgroup') {
        return Children.map(child.props.children, function (option) {
          return option;
        });
      }
      return child;
    });

    // Iterate through our array to determine which option is the "selected" one
    var selectedOption;
    var firstOption;
    for (var i = 0; i < options.length; i += 1) {
      // An option's label text can comes from either its `label` prop or its
      // text node (`children`)
      var optionLabel = options[i].props.label || options[i].props.children;

      // An option's value can come from its `value` prop or its label
      var optionValue = options[i].props.value || optionLabel;
      var optionObj = {
        value: optionValue,
        label: optionLabel
      };

      // Store first option's info in case we need it later
      if (typeof firstOption === 'undefined' && !options[i].props.disabled) {
        firstOption = optionObj;
      }

      // If this is the selected option...
      if (optionValue === value) {
        selectedOption = optionObj;
        break;
      }
    }

    // If no value or defaultValue was given, default to the first option
    if (typeof selectedOption === 'undefined') {
      selectedOption = firstOption;
    }
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(StyledReadOnlySelect, {
      forwardedAs: Input,
      readOnly: true,
      value: typeof selectedOption !== 'undefined' ? selectedOption.label : '',
      type: "text"
    }), /*#__PURE__*/React.createElement("input", _extends({
      type: "hidden",
      value: typeof selectedOption !== 'undefined' ? selectedOption.value : '',
      ref: forwardedRef
    }, rest)));
  }

  // If not read-only, return a normal `select` element
  return /*#__PURE__*/React.createElement(StyledReadOnlySelect, _extends({
    onChange: onInputChange,
    ref: forwardedRef,
    value: value
  }, rest), children);
});
ReadOnlySelect.displayName = 'ReadOnlySelect';
ReadOnlySelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The default value when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultValue: PropTypes.string,
  /**
   * The value when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  value: PropTypes.string,
  /**
   * `<option>` and `<optgroup>` nodes
   */
  children: PropTypes.node.isRequired,
  /**
   * Set the select as read-only.
   * Can be controlled by `FormField` context.
   */
  readOnly: PropTypes.bool,
  /**
   * A callback that is called with the selected option's value.
   */
  onChange: PropTypes.func
} : {};